import {
  isObject,
  ROUTED_ATTRIBUTE,
  sourceOperations,
} from "@/pages/pipelineOperations";
import type { Service } from "@/types/api";
import { PROVIDER_LABELS } from "@/utils/providerCapabilities";
import type { Path, PipelineDocError } from "./pipelineLint";

// Whether a pipeline's source can take the filter written for it. Needs the
// source's provider, so it runs with the reference checks, not the
// structural ones.

/** What a scanner reports as the operation of every record it reads. */
const SNAPSHOT = "snapshot";

/** A readable source filter: the operations it keeps, and where in the
 *  document it is written. */
export interface SourceFilter {
  operations: string[];
  path: Path;
}

/** The source's filter when it is well formed; a malformed one is the
 *  structural lint's to report. */
export function sourceFilterOf(doc: unknown): SourceFilter | null {
  if (!isObject(doc)) return null;
  const taken = sourceOperations(doc);
  if (taken.kind !== "only") return null;
  const listed =
    doc.operation_types !== undefined && doc.operation_types !== null;
  return {
    operations: taken.operations,
    path: listed ? ["operation_types"] : ["source", "route_id"],
  };
}

/** Sources whose records state their operation. */
const FILTERABLE = new Set([
  "mongodb",
  "postgres_cdc",
  "http_source",
  "pubsub",
]);
/** Sources whose records carry no operation at all. */
const UNFILTERABLE = new Set(["kafka", "generator"]);

/** Why a source of this provider cannot take the filter, or null when it
 *  can. Only called with a provider the service list really returned; a
 *  provider this does not know is left to the engine. */
export function sourceFilterError(
  filter: SourceFilter,
  provider: string,
): PipelineDocError | null {
  if (FILTERABLE.has(provider)) return null;
  const label = PROVIDER_LABELS[provider as Service["provider"]] ?? provider;
  if (provider === "scanner") {
    if (filter.operations.includes(SNAPSHOT)) return null;
    return {
      path: filter.path,
      message: `A ${label} source reports every record as "${SNAPSHOT}": the filter must list "${SNAPSHOT}", or the scan keeps nothing`,
    };
  }
  if (!UNFILTERABLE.has(provider)) return null;
  return {
    path: filter.path,
    message: `A ${label} source carries no ${ROUTED_ATTRIBUTE}, so it cannot be filtered. Route its records at a middleware or sink instead`,
  };
}
